import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch
} from '@mui/material';
import {
  dialogFormPaperSx,
  dialogFormScrollContentSx,
  dialogFormTitleSx,
  dialogFormActionsSx
} from '../utils/dialogFormLayout';

const sanitizeCodeInput = (value) => value.replace(/[^a-zA-Z0-9]/g, '').toUpperCase().slice(0, 40);

const PromoCodeForm = ({ open, onClose, onSubmit, promo, isEdit, characters = [] }) => {
  const [code, setCode] = useState('');
  const [characterId, setCharacterId] = useState('');
  const [relationshipBonus, setRelationshipBonus] = useState('0');
  const [active, setActive] = useState(true);

  useEffect(() => {
    if (promo && isEdit) {
      setCode(promo.code || '');
      const cid = promo.characterId?._id || promo.characterId;
      setCharacterId(cid ? String(cid) : '');
      setRelationshipBonus(String(promo.relationshipBonus ?? 0));
      setActive(promo.active !== false);
    } else {
      setCode('');
      setCharacterId('');
      setRelationshipBonus('0');
      setActive(true);
    }
  }, [promo, isEdit, open]);

  const handleSubmit = (e) => {
    e.preventDefault();
    const bonus = parseInt(relationshipBonus, 10);
    onSubmit({
      code: sanitizeCodeInput(code),
      characterId,
      relationshipBonus: Number.isFinite(bonus) ? bonus : 0,
      active
    });
  };

  const handleClose = () => {
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: dialogFormPaperSx }}
    >
      <DialogTitle sx={dialogFormTitleSx}>
        {isEdit ? 'Edit Promo Code' : 'Create Promo Code'}
      </DialogTitle>
      <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', flexDirection: 'column', flex: '1 1 auto', minHeight: 0, overflow: 'hidden' }}>
        <DialogContent sx={(theme) => dialogFormScrollContentSx(theme)}>
          <TextField
            label="Code (A–Z, 0–9)"
            value={code}
            onChange={(e) => setCode(sanitizeCodeInput(e.target.value))}
            fullWidth
            required
            disabled={isEdit}
            helperText={isEdit ? 'Code cannot be changed' : '3–40 characters, stored uppercase'}
            sx={{ mb: 2 }}
          />
          <FormControl fullWidth required sx={{ mb: 2 }}>
            <InputLabel>Character</InputLabel>
            <Select
              value={characterId}
              label="Character"
              onChange={(e) => setCharacterId(e.target.value)}
            >
              {characters.map((c) => (
                <MenuItem key={c._id} value={String(c._id)}>
                  {c.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            label="Relationship bonus (RP)"
            type="number"
            value={relationshipBonus}
            onChange={(e) => setRelationshipBonus(e.target.value)}
            fullWidth
            required
            inputProps={{ min: 0 }}
            helperText="Applied on the client to this character’s relationship value"
            sx={{ mb: 2 }}
          />
          <FormControlLabel
            control={<Switch checked={active} onChange={(e) => setActive(e.target.checked)} />}
            label="Active (inactive codes cannot be redeemed)"
          />
        </DialogContent>
        <DialogActions sx={dialogFormActionsSx}>
          <Button onClick={handleClose}>Cancel</Button>
          <Button type="submit" variant="contained" disabled={!code || code.length < 3 || !characterId}>
            {isEdit ? 'Save' : 'Create'}
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
};

export default PromoCodeForm;
